import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { Patient } from '@models/patient';
import { AuthService } from '@services/auth.service';
import { NotificationService } from '@services/notification.service';
import { REPAIR_ITEM_OPTIONS } from '../../../../constants/repair-items';
import {
  IN_HOUSE_REPAIR_LOCATION,
} from '../../../../constants/task-lists';
import { exclusiveJobAction, jobActionList, toggleJobAction } from '@classes/job-actions';
import { SettingsService } from '@services/settings.service';
import {
  CompleteTaskInput,
  TaskImage,
  WorkflowContext,
  WorkflowEntry,
  WorkflowResult,
  activityEntryDefaults,
  awaitingActions,
  awaitingFields,
  awaitingRows,
  canReopenCompleted,
  canonicalOpenStatus,
  classifyKind,
  completeOpenTask,
  completedHistoryLine,
  completedRows,
  editAwaiting,
  isValidInitials,
  normalizeInitials,
  normalizeWorkflowEntries,
  openTaskActions,
  openTaskFields,
  openTaskRows,
  reopenCompleted,
  resolveAwaiting,
} from '@classes/task-activity';
import { PRODUCTION_OPEN_STATUSES } from '@classes/job-ready';

interface TaskRowView {
  index: number;
  entry: WorkflowEntry;
  label: string;
  fields: string[];
  actions: string[];
  form: FormGroup;
  editing?: boolean;
}

@Component({
  selector: 'app-task-activity',
  templateUrl: './task-activity.component.html',
  styleUrls: ['./task-activity.component.css'],
})
export class TaskActivityComponent implements OnInit, OnChanges, OnDestroy {
  @Input() orderForm: FormGroup;
  @Input() patient: Patient;
  @Input() locked = false;
  @Output() saveOrder = new EventEmitter<{ destination: string }>();

  readonly repairItemOptions = REPAIR_ITEM_OPTIONS;
  repairLocations: string[] = [IN_HOUSE_REPAIR_LOCATION];

  openRows: TaskRowView[] = [];
  awaiting: TaskRowView[] = [];
  completed: TaskRowView[] = [];

  constructor(
    private fb: FormBuilder,
    private auth: AuthService,
    private notificationService: NotificationService,
    private settingsService: SettingsService
  ) {}

  ngOnInit(): void {
    this.repairLocations = this.settingsService.activeRepairLocations();
    this.settingsService.loadSettings().subscribe({
      next: () => {
        this.repairLocations = this.settingsService.activeRepairLocations();
      },
      error: () => {},
    });
    this.normalizeAndRebuild();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.orderForm && !changes.orderForm.firstChange) {
      this.normalizeAndRebuild();
    }
  }

  ngOnDestroy(): void {}

  get activityFormArray(): FormArray {
    return this.orderForm?.get('products.0.frame.activity') as FormArray;
  }

  get defaultInitials(): string {
    const formInitials = String(this.orderForm?.get('initials')?.value || '').trim();
    if (formInitials) {
      return formInitials.toUpperCase();
    }
    const userInitials = String(this.auth?.currentUser?.initials || '').trim();
    if (userInitials) {
      return userInitials.toUpperCase();
    }
    return String(this.auth?.initials || '').trim().toUpperCase();
  }

  waitingOnOptions = ['Doctor', 'Customer'];

  awaitingChoices(row: TaskRowView): string[] {
    return awaitingActions(row.entry.status);
  }

  taskImages(row: TaskRowView, kind: 'condition' | 'invoice'): TaskImage[] {
    const images = row.form.get('images')?.value || [];
    return (Array.isArray(images) ? images : []).filter(
      (image: TaskImage) => (image?.kind || 'condition') === kind
    );
  }

  onTaskImageSelected(
    row: TaskRowView,
    kind: 'condition' | 'invoice',
    event: Event
  ): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input?.files || []);
    if (!files.length) {
      return;
    }
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        const current = Array.isArray(row.form.get('images')?.value)
          ? row.form.get('images').value.slice()
          : [];
        current.push({
          name: file.name,
          dataUrl: String(reader.result || ''),
          kind,
        });
        row.form.get('images')?.setValue(current);
      };
      reader.readAsDataURL(file);
    });
    input.value = '';
  }

  removeTaskImage(row: TaskRowView, image: TaskImage): void {
    const current = Array.isArray(row.form.get('images')?.value)
      ? row.form.get('images').value.slice()
      : [];
    row.form.get('images')?.setValue(
      current.filter((item: TaskImage) => item !== image)
    );
  }

  contactLine(waitingOn?: string): string {
    if (!this.patient) {
      return '';
    }
    if (waitingOn === 'Doctor') {
      return [
        this.patient.doctorName,
        this.patient.doctorPhone,
        this.patient.doctorFax,
        this.patient.doctorEmail,
      ]
        .map((value) => String(value || '').trim())
        .filter(Boolean)
        .join('  ·  ');
    }
    return [this.patient.cell, this.patient.phone, this.patient.email]
      .map((value) => String(value || '').trim())
      .filter(Boolean)
      .join('  ·  ');
  }

  hasField(row: TaskRowView, field: string): boolean {
    return row.fields.includes(field);
  }

  isRepairItemSelected(row: TaskRowView, item: string): boolean {
    const selected = row.form.get('repairItems')?.value || [];
    return Array.isArray(selected) && selected.includes(item);
  }

  toggleRepairItem(row: TaskRowView, item: string, checked: boolean): void {
    const control = row.form.get('repairItems');
    const current = Array.isArray(control?.value) ? control.value.slice() : [];
    const next = current.filter((value) => value !== item);
    if (checked) {
      next.push(item);
    }
    control?.setValue(next);
  }

  completedLine(row: TaskRowView): string {
    return completedHistoryLine(row.entry);
  }

  canReopen(row: TaskRowView): boolean {
    return canReopenCompleted(row.entry);
  }

  toggleAwaitingEdit(row: TaskRowView): void {
    if (this.locked) {
      return;
    }
    row.editing = !row.editing;
  }

  referenceLabel(row: TaskRowView): string {
    const status = canonicalOpenStatus(row.entry?.status);
    if (status === 'REPAIR' || status === 'CUSTOM LENSES') {
      return 'ORDER #';
    }
    return 'VENDOR REFERENCE #';
  }

  appendTaskNote(row: TaskRowView): void {
    const draft = String(row.form.get('noteDraft')?.value || '').trim();
    if (!draft) {
      return;
    }
    const current = String(row.form.get('notes')?.value || '').trim();
    row.form.get('notes')?.setValue(current ? `${current}\n${draft}` : draft);
    row.form.get('noteDraft')?.setValue('');
  }

  onRepairPriceBlur(row: TaskRowView): void {
    const items = row.form.get('repairItems')?.value || [];
    if (!Array.isArray(items) || !items.includes('Other')) {
      return;
    }
    const price = row.form.get('price')?.value;
    const control = this.orderForm?.get('products.0.frame.partOtherPrice');
    if (!control) {
      return;
    }
    control.setValue(price);
    control.markAsDirty();
  }

  saveOpenTask(row: TaskRowView): void {
    if (this.locked) {
      return;
    }
    this.appendTaskNote(row);
    const initials = this.requireInitials(row);
    if (!initials) {
      return;
    }
    const input = this.inputFromRow(row, initials);
    const result = completeOpenTask(this.currentEntries(), input, this.workflowContext());
    this.commitResult(result, initials);
  }

  saveAwaiting(row: TaskRowView): void {
    if (this.locked) {
      return;
    }
    this.appendTaskNote(row);
    const initials = this.requireInitials(row);
    if (!initials) {
      return;
    }
    const choice = String(row.form.get('activityChoice')?.value || '').trim();
    const input = this.inputFromRow(row, initials);
    const result = choice
      ? resolveAwaiting(this.currentEntries(), input, this.workflowContext())
      : editAwaiting(this.currentEntries(), input);
    if (choice && !result.logItem) {
      this.notificationService.show('error', 'SELECT AN ACTIVITY', 'OK');
      return;
    }
    this.commitResult(result, initials);
  }

  reopenCompletedTask(row: TaskRowView): void {
    if (this.locked) {
      return;
    }
    const initials = this.requireInitials(row);
    if (!initials) {
      return;
    }
    const result = reopenCompleted(this.currentEntries(), {
      id: row.entry.id,
      index: row.index,
      initials,
      notes: row.form.get('notes')?.value,
      repairItems: row.form.get('repairItems')?.value,
    });
    this.commitResult(result, initials);
  }

  private requireInitials(row: TaskRowView): string {
    const initials = normalizeInitials(
      row.form.get('initials')?.value || this.defaultInitials
    );
    if (!isValidInitials(initials)) {
      this.notificationService.show(
        'error',
        'ENTER VALID EMPLOYEE INITIALS TO SAVE',
        'OK'
      );
      return '';
    }
    row.form.get('initials')?.setValue(initials);
    this.orderForm?.get('initials')?.setValue(initials);
    return initials;
  }

  private inputFromRow(row: TaskRowView, initials: string): CompleteTaskInput {
    const value = row.form.getRawValue();
    return {
      id: row.entry.id,
      index: row.index,
      initials,
      notes: value.notes,
      repairItems: value.repairItems,
      price: value.price,
      eta: value.eta,
      referenceNumber: value.referenceNumber,
      followUpDate: value.followUpDate,
      waitingOn: value.waitingOn,
      vendor: value.vendor,
      labVendor: value.labVendor,
      carrier: value.carrier,
      trackingNumber: value.trackingNumber,
      activityChoice: value.activityChoice,
      description: value.description,
      expectedDate: value.expectedDate,
      repairLocation: value.repairLocation,
      estimate: value.estimate,
      estimateApprovalRequired: !!value.estimateApprovalRequired,
      otherText: value.otherText,
      whoToCall: value.whoToCall,
      images: value.images,
    };
  }

  private workflowContext(): WorkflowContext {
    return {
      jobActions: jobActionList(
        this.orderForm?.get('jobAction')?.value,
        this.orderForm?.get('jobActions')?.value
      ),
      status: this.orderForm?.get('status')?.value,
      physicalStatus: this.orderForm?.get('products.0.frame.physicalStatus')?.value,
      lensStatus: this.orderForm?.get('lensStatus')?.value,
      nosepadInstall: !!this.orderForm?.get('products.0.frame.nosepadInstall')?.value,
      partOther: !!this.orderForm?.get('products.0.frame.partOther')?.value,
      partOtherDescription: this.orderForm?.get(
        'products.0.frame.partOtherDescription'
      )?.value,
      repairItems: this.orderForm?.get('products.0.frame.repairItems')?.value || [],
      enclosed: !!this.orderForm?.get('products.0.frame.enclosed')?.value,
      frameOnly: !!this.orderForm?.get('frameOnly')?.value,
      followUpDate: this.orderForm?.get('products.0.frame.followUpDate')?.value,
      eta: this.orderForm?.get('eta')?.value,
      referenceNumber: this.orderForm?.get('products.0.frame.referenceNumber')?.value,
      labVendor: this.orderForm?.get('products.0.frame.labVendor')?.value,
      inquiryFollowUpHours: this.settingsService.inquiryFollowUpHours,
      awaitingRxFollowUpHours: this.settingsService.awaitingRxFollowUpHours,
    };
  }

  private currentEntries(): WorkflowEntry[] {
    return (this.activityFormArray?.getRawValue() || []).map((entry) =>
      activityEntryDefaults(entry)
    );
  }

  normalizeAndRebuild(): void {
    const activity = this.activityFormArray;
    if (!this.orderForm || !activity) {
      this.openRows = [];
      this.awaiting = [];
      this.completed = [];
      return;
    }
    const current = activity.getRawValue() || [];
    const normalized = normalizeWorkflowEntries(current, this.workflowContext());
    if (this.materialSignature(current) !== this.materialSignature(normalized)) {
      this.writeEntries(normalized);
    }
    this.rebuildViews();
  }

  private materialSignature(entries: WorkflowEntry[]): string {
    return (entries || [])
      .map((entry) => {
        const value = activityEntryDefaults(entry);
        return [
          value.kind,
          canonicalOpenStatus(value.status) || value.status,
          value.completed ? '1' : '0',
        ].join(':');
      })
      .join('|');
  }

  private commitResult(result: WorkflowResult, initials: string): void {
    if (!result?.logItem && result.destination === 'SAVE') {
      this.writeEntries(result.entries);
      this.rebuildViews();
      return;
    }
    this.writeEntries(result.entries);
    this.applyPatch(result.patch, initials);
    const latestCompleted = [...result.entries]
      .reverse()
      .find((entry) => entry.kind === 'completed' && Array.isArray(entry.repairItems));
    if (latestCompleted?.repairItems?.length) {
      this.orderForm
        ?.get('products.0.frame.repairItems')
        ?.setValue(latestCompleted.repairItems);
    }
    if (/^FOLLOWED UP \/ FRAME/i.test(result.logItem)) {
      this.orderForm?.get('products.0.frame.etaChecked')?.setValue(true);
      this.orderForm?.get('products.0.frame.etaCheckedBy')?.setValue(initials);
      this.orderForm?.get('products.0.frame.etaCheckedAt')?.setValue(new Date());
    }
    this.appendLog(result.logItem, result.logNotes, initials);
    this.maybeMoveToJobReady(result.entries);
    this.rebuildViews();
    this.saveOrder.emit({ destination: result.destination || 'SAVE' });
  }

  private maybeMoveToJobReady(entries: WorkflowEntry[]): void {
    const fulfillment = String(this.orderForm?.get('fulfillment')?.value || 'PICKUP')
      .trim()
      .toUpperCase();
    if (fulfillment === 'SHIP') {
      return;
    }
    const status = String(this.orderForm?.get('status')?.value || '').trim().toUpperCase();
    if (status === 'DISPENSED' || status === 'CANCELED' || status === 'VOID') {
      return;
    }
    const stillOpen = (entries || []).some((entry) => {
      if (entry?.completed || classifyKind(entry) !== 'open') {
        return false;
      }
      const task = String(
        canonicalOpenStatus(entry.status) || entry.status || ''
      )
        .trim()
        .toUpperCase();
      return PRODUCTION_OPEN_STATUSES.indexOf(task) !== -1;
    });
    if (stillOpen) {
      return;
    }
    this.orderForm?.get('status')?.setValue('RECEIVED');
    const dateReceived = this.orderForm?.get('products.0.frame.dateReceived');
    if (dateReceived && !dateReceived.value) {
      dateReceived.setValue(new Date());
    }
  }

  private applyPatch(patch: WorkflowResult['patch'], initials: string): void {
    if (!patch) {
      return;
    }
    if (patch.eta !== undefined && patch.eta !== '') {
      this.orderForm?.get('eta')?.setValue(patch.eta);
    }
    if (patch.referenceNumber !== undefined) {
      this.orderForm
        ?.get('products.0.frame.referenceNumber')
        ?.setValue(patch.referenceNumber);
    }
    if (patch.followUpDate !== undefined) {
      this.orderForm
        ?.get('products.0.frame.followUpDate')
        ?.setValue(patch.followUpDate || '');
    }
    if (patch.labVendor !== undefined) {
      this.orderForm?.get('products.0.frame.labVendor')?.setValue(patch.labVendor);
    }
    if (patch.physicalStatus !== undefined) {
      this.orderForm
        ?.get('products.0.frame.physicalStatus')
        ?.setValue(patch.physicalStatus);
    }
    if (patch.lensStatus !== undefined) {
      this.orderForm?.get('lensStatus')?.setValue(patch.lensStatus);
    }
    if (patch.rxStatus !== undefined) {
      this.orderForm?.get('rxStatus')?.setValue(patch.rxStatus);
    }
    if (patch.status) {
      this.orderForm?.get('status')?.setValue(patch.status);
    }
    if (patch.todo) {
      this.orderForm?.get('todo')?.setValue(patch.todo);
    }
    if (Array.isArray(patch.jobActionsRemove) && patch.jobActionsRemove.length) {
      let actions = jobActionList(
        this.orderForm?.get('jobAction')?.value,
        this.orderForm?.get('jobActions')?.value
      );
      patch.jobActionsRemove.forEach((action) => {
        actions = toggleJobAction(actions, action, false);
      });
      this.orderForm?.get('jobActions')?.setValue(actions);
      this.orderForm?.get('jobAction')?.setValue(exclusiveJobAction(actions));
    }
    if (patch.destination && patch.destination !== 'SAVE') {
      this.orderForm
        ?.get('products.0.frame.activityStatus')
        ?.setValue(this.activityStatusForDestination(patch.destination));
    }
    this.orderForm?.get('initials')?.setValue(initials);
  }

  private activityStatusForDestination(destination: string): string {
    switch (destination) {
      case 'ORDERED FRAME':
      case 'ORDERED':
        return 'ORDERED';
      case 'CALLED FOR RX':
        return 'CALLED FOR RX';
      case 'RX RECEIVED':
        return 'RX RECEIVED';
      case 'RECEIVED FRAME':
        return 'RECEIVED';
      case 'ORDERED LENSES':
        return 'ORDERED LENSES';
      case 'RECEIVED LENSES':
        return 'RECEIVED LENSES';
      case 'SENT FOR REPAIR':
        return 'SENT FOR REPAIR';
      case 'SHIPPED':
        return 'SHIPPED';
      default:
        return destination;
    }
  }

  private appendLog(item: string, notes: string, initials: string): void {
    if (!item) {
      return;
    }
    const log = this.orderForm?.get('log') as FormArray;
    if (!log) {
      return;
    }
    log.insert(
      0,
      this.fb.group({
        _id: null,
        initials,
        user: this.auth?.currentUser?._id || '',
        item,
        notes: notes || '',
        date: Date.now().toString(),
      })
    );
  }

  private writeEntries(entries: WorkflowEntry[]): void {
    const activity = this.activityFormArray;
    if (!activity) {
      return;
    }
    while (activity.length) {
      activity.removeAt(0);
    }
    entries.forEach((entry) => {
      activity.push(this.entryGroup(entry));
    });
    activity.markAsDirty();
  }

  private entryGroup(entry: WorkflowEntry): FormGroup {
    const value = activityEntryDefaults(entry);
    return this.fb.group({
      id: value.id,
      kind: value.kind,
      date: value.date,
      status: value.status,
      branch: value.branch,
      notes: value.notes,
      repairItems: [value.repairItems || []],
      waitingOn: value.waitingOn,
      vendor: value.vendor,
      followUpDate: value.followUpDate,
      expectedDate: value.expectedDate,
      followUpNumber: value.followUpNumber,
      activityChoice: value.activityChoice,
      reopenedFrom: value.reopenedFrom,
      sourceId: value.sourceId,
      description: value.description,
      referenceNumber: value.referenceNumber,
      carrier: value.carrier,
      trackingNumber: value.trackingNumber,
      labVendor: value.labVendor,
      repairLocation: value.repairLocation,
      estimate: value.estimate,
      price: value.price,
      estimateApprovalRequired: value.estimateApprovalRequired,
      approvalStatus: value.approvalStatus,
      otherText: value.otherText,
      whoToCall: value.whoToCall,
      images: [value.images || []],
      eta: value.eta,
      completed: value.completed,
      completedBy: value.completedBy,
      completedAt: value.completedAt,
    });
  }

  private rebuildViews(): void {
    const entries = this.currentEntries();
    this.openRows = openTaskRows(entries).map((row) => this.toView(row, 'open'));
    this.awaiting = awaitingRows(entries).map((row) => this.toView(row, 'awaiting'));
    this.completed = completedRows(entries)
      .filter((row) => classifyKind(row.entry) === 'completed')
      .map((row) => this.toView(row, 'completed'));
  }

  private toView(
    row: { index: number; entry: WorkflowEntry },
    kind: 'open' | 'awaiting' | 'completed'
  ): TaskRowView {
    const entry = row.entry;
    const fields =
      kind === 'awaiting'
        ? awaitingFields(entry.status)
        : kind === 'open'
        ? openTaskFields(entry.status)
        : ['notes', 'repairItems'];
    const actions =
      kind === 'open'
        ? openTaskActions(entry.status, entry)
        : kind === 'awaiting'
        ? awaitingActions(entry.status)
        : [];
    return {
      index: row.index,
      entry,
      label: canonicalOpenStatus(entry.status) || String(entry.status || ''),
      fields,
      actions,
      editing: false,
      form: this.fb.group({
        initials: this.defaultInitials,
        notes: entry.notes || '',
        repairItems: [entry.repairItems || []],
        eta: entry.eta || this.orderForm?.get('eta')?.value || '',
        referenceNumber:
          entry.referenceNumber ||
          this.orderForm?.get('products.0.frame.referenceNumber')?.value ||
          '',
        followUpDate:
          entry.followUpDate ||
          this.orderForm?.get('products.0.frame.followUpDate')?.value ||
          '',
        expectedDate: entry.expectedDate || '',
        waitingOn: entry.waitingOn || '',
        vendor: entry.vendor || '',
        labVendor:
          entry.labVendor ||
          this.orderForm?.get('products.0.frame.labVendor')?.value ||
          '',
        repairLocation: entry.repairLocation || IN_HOUSE_REPAIR_LOCATION,
        estimate: entry.estimate || '',
        price: entry.price || '',
        noteDraft: '',
        estimateApprovalRequired: !!entry.estimateApprovalRequired,
        otherText: entry.otherText || '',
        whoToCall: entry.whoToCall || '',
        images: [entry.images || []],
        carrier: entry.carrier || '',
        trackingNumber: entry.trackingNumber || '',
        activityChoice: kind === 'open' && actions.length ? actions[0] : '',
        description: entry.description || '',
      }),
    };
  }
}
